/**
 * Beastform Service - Manages Druid transformation mechanics
 *
 * Handles:
 * - Loading beastform data
 * - Transformation (activate/drop)
 * - Stat calculations
 * - Special form builders (Legendary/Hybrid)
 * - Evolution hope feature
 */

import toast from 'react-hot-toast';
import { hasClass } from '../utils/featureDetection';

// Cache for loaded beastform data
let beastformDataCache = null;

/**
 * Load beastform data from JSON
 * @returns {Promise<Object>} Beastform data
 */
export const loadBeastformData = async () => {
  if (beastformDataCache) {
    return beastformDataCache;
  }

  try {
    const response = await fetch('/data/core_data/beastforms.json');
    if (!response.ok) {
      throw new Error('Failed to load beastform data');
    }
    beastformDataCache = await response.json();
    return beastformDataCache;
  } catch (error) {
    console.error('Error loading beastform data:', error);
    return { beastforms: [], tiers: {}, specialForms: {} };
  }
};

/**
 * Get the tier number a character can access based on level
 * @param {number} level - Character level
 * @returns {number} Maximum accessible tier (1-4)
 */
export const getMaxTierForLevel = (level) => {
  if (level >= 9) return 4;
  if (level >= 6) return 3;
  if (level >= 3) return 2;
  return 1;
};

/**
 * Get all available beastforms for a character's level
 * @param {number} characterLevel - Character level (1-10)
 * @returns {Promise<Array>} Array of available beastforms
 */
export const getAvailableForms = async (characterLevel) => {
  const data = await loadBeastformData();
  const maxTier = getMaxTierForLevel(characterLevel);

  return data.beastforms.filter(form => form.tier <= maxTier);
};

/**
 * Get forms grouped by tier
 * @param {number} characterLevel - Character level
 * @returns {Promise<Object>} Forms grouped by tier { 1: [...], 2: [...], etc }
 */
export const getFormsGroupedByTier = async (characterLevel) => {
  const forms = await getAvailableForms(characterLevel);
  const grouped = {};

  forms.forEach(form => {
    if (!grouped[form.tier]) {
      grouped[form.tier] = [];
    }
    grouped[form.tier].push(form);
  });

  return grouped;
};

/**
 * Get a specific beastform by ID
 * @param {string} formId - Beastform ID
 * @returns {Promise<Object|null>} Beastform data or null
 */
export const getFormById = async (formId) => {
  const data = await loadBeastformData();
  return data.beastforms.find(form => form.id === formId) || null;
};

/**
 * Get special form builder configuration
 * @param {string} builderId - Builder ID ('legendaryBeast', 'legendaryHybrid', etc.)
 * @returns {Promise<Object|null>} Builder config or null
 */
export const getSpecialFormBuilder = async (builderId) => {
  const data = await loadBeastformData();
  return data.specialForms?.[builderId] || null;
};

/**
 * Check if a character can transform into a beastform
 * @param {Object} character - Character data
 * @param {string} formId - Beastform ID
 * @param {Object} options - { useEvolution: boolean }
 * @returns {Object} { canTransform: boolean, reason: string }
 */
export const canTransform = async (character, formId, options = {}) => {
  // Must be a Druid
  if (!hasClass(character, 'druid')) {
    return { canTransform: false, reason: 'Only Druids can use Beastform' };
  }

  // Can't transform if already transformed
  if (character.activeBeastform) {
    return { canTransform: false, reason: 'Already in Beastform - drop current form first' };
  }

  // Check form exists and is accessible
  const form = await getFormById(formId);
  if (!form) {
    return { canTransform: false, reason: 'Unknown beastform' };
  }

  const maxTier = getMaxTierForLevel(character.level || 1);
  if (form.tier > maxTier) {
    return { canTransform: false, reason: `Form requires level ${form.tier === 2 ? 3 : form.tier === 3 ? 6 : 9}+` };
  }

  // Check costs
  if (options.useEvolution) {
    // Evolution: 3 Hope, no Stress
    if ((character.hope || 0) < 3) {
      return { canTransform: false, reason: 'Evolution requires 3 Hope' };
    }
  } else {
    // Normal: 1 Stress
    if ((character.stress || 0) >= (character.maxStress || 6)) {
      return { canTransform: false, reason: 'Cannot mark more Stress' };
    }
  }

  return { canTransform: true, reason: null };
};

/**
 * Build the activeBeastform state object
 * @param {Object} character - Character data
 * @param {Object} form - Beastform data
 * @param {Object} options - Transformation options
 * @returns {Object} Active beastform state
 */
const buildActiveBeastformState = (character, form, options = {}) => {
  const { useEvolution, evolutionTrait, customName, isBuilt, baseFormId, baseFormIds } = options;

  return {
    formId: form.id,
    formName: form.name,
    customName: customName || null,
    traitBonuses: { ...form.traitBonus },
    evasionBonus: form.evasionBonus,
    attack: {
      ...form.attack,
      proficiency: character.proficiency || 2
    },
    advantages: [...(form.advantages || [])],
    features: [...(form.features || [])],
    evolutionBonus: useEvolution && evolutionTrait ? { trait: evolutionTrait, amount: 1 } : null,
    isBuilt: isBuilt || false,
    baseFormId: baseFormId || null,
    baseFormIds: baseFormIds || null,
    activatedAt: Date.now()
  };
};

/**
 * Transform into a beastform
 * @param {Object} character - Character data
 * @param {string} formId - Beastform ID
 * @param {Object} options - { useEvolution, evolutionTrait, customName, isBuilt, builtForm }
 * @param {Function} updateCharacter - Function to update character
 * @returns {Promise<Object>} Updated character or error
 */
export const transform = async (character, formId, options = {}, updateCharacter) => {
  const { useEvolution, evolutionTrait, customName, isBuilt, builtForm } = options;

  // Must be a Druid
  if (!hasClass(character, 'druid')) {
    toast.error('Only Druids can use Beastform');
    return { success: false, error: 'Only Druids can use Beastform' };
  }

  // Can't transform if already transformed
  if (character.activeBeastform) {
    toast.error('Already in Beastform - drop current form first');
    return { success: false, error: 'Already in beastform' };
  }

  let form;
  let stressCost = useEvolution ? 0 : 1;
  let hopeCost = useEvolution ? 3 : 0;

  if (isBuilt && builtForm) {
    // Use the pre-built special form
    form = builtForm;
    // Built forms may have extra stress cost (hybrids)
    stressCost = builtForm.stressCost || stressCost;
  } else {
    // Validate normal form
    const validation = await canTransform(character, formId, options);
    if (!validation.canTransform) {
      toast.error(validation.reason);
      return { success: false, error: validation.reason };
    }

    // Get form data
    form = await getFormById(formId);
    if (!form) {
      toast.error('Form not found');
      return { success: false, error: 'Form not found' };
    }
  }

  // Build active beastform state
  const activeBeastform = buildActiveBeastformState(character, form, {
    ...options,
    isBuilt: isBuilt || false,
    baseFormId: builtForm?.baseFormId,
    baseFormIds: builtForm?.baseFormIds
  });

  // Build updated character
  const updatedCharacter = {
    ...character,
    activeBeastform,
    stress: (character.stress || 0) + stressCost,
    hope: (character.hope || 0) - hopeCost
  };

  // Persist to Firebase
  try {
    await updateCharacter(updatedCharacter);

    const message = isBuilt
      ? `Transformed into ${form.name}! ⭐`
      : useEvolution
        ? `Evolved into ${form.name}! +1 ${evolutionTrait}`
        : `Transformed into ${form.name}!`;
    toast.success(message, { icon: '🐾' });

    return { success: true, character: updatedCharacter };
  } catch (error) {
    console.error('Error transforming:', error);
    toast.error('Failed to transform');
    return { success: false, error: error.message };
  }
};

/**
 * Drop out of beastform
 * @param {Object} character - Character data
 * @param {string} reason - Drop reason ('voluntary', 'last-hp', 'fragile-damage')
 * @param {Function} updateCharacter - Function to update character
 * @returns {Promise<Object>} Updated character or error
 */
export const dropForm = async (character, reason, updateCharacter) => {
  if (!character.activeBeastform) {
    return { success: false, error: 'Not in beastform' };
  }

  const formName = character.activeBeastform.formName;

  // Build updated character
  const updatedCharacter = {
    ...character,
    activeBeastform: null
  };

  try {
    await updateCharacter(updatedCharacter);

    // Show appropriate message based on reason
    const messages = {
      'voluntary': `Dropped ${formName}`,
      'last-hp': `Dropped ${formName} - hit point limit reached!`,
      'fragile-damage': `${formName} shattered from Major damage!`
    };
    toast(messages[reason] || `Dropped ${formName}`, { icon: '🐾' });

    return { success: true, character: updatedCharacter };
  } catch (error) {
    console.error('Error dropping form:', error);
    toast.error('Failed to drop form');
    return { success: false, error: error.message };
  }
};

/**
 * Calculate transformed stats for display
 * @param {Object} character - Character data
 * @param {Object} activeBeastform - Active beastform state (or null)
 * @returns {Object} Transformed stats { evasionBonus, traitBonuses, attack }
 */
export const getTransformedStats = (character, activeBeastform = null) => {
  const form = activeBeastform || character.activeBeastform;
  if (!form) return null;

  return {
    evasionBonus: form.evasionBonus || 0,
    traitBonuses: form.traitBonuses || {},
    evolutionBonus: form.evolutionBonus,
    attack: form.attack,
    advantages: form.advantages || [],
    features: form.features || []
  };
};

/**
 * Check if character has a specific beastform feature
 * @param {Object} character - Character data
 * @param {string} featureId - Feature ID to check
 * @returns {boolean} True if feature is active
 */
export const hasBeastformFeature = (character, featureId) => {
  if (!character.activeBeastform) return false;
  return character.activeBeastform.features?.some(f => f.id === featureId) || false;
};

/**
 * Check if character has a specific beastform mechanic
 * @param {Object} character - Character data
 * @param {string} mechanicId - Mechanic ID to check (e.g., 'dropOnMajorDamage')
 * @returns {boolean} True if mechanic is active
 */
export const hasBeastformMechanic = (character, mechanicId) => {
  if (!character.activeBeastform) return false;
  return character.activeBeastform.features?.some(f => f.mechanic === mechanicId) || false;
};

/**
 * Get threshold bonus/penalty from beastform
 * @param {Object} character - Character data
 * @returns {number} Net threshold modifier
 */
export const getBeastformThresholdModifier = (character) => {
  if (!character.activeBeastform) return 0;

  let modifier = 0;
  const features = character.activeBeastform.features || [];

  features.forEach(feature => {
    if (feature.mechanic === 'thresholdBonus' && feature.value) {
      modifier += feature.value;
    }
    if (feature.mechanic === 'thresholdPenalty' && feature.value) {
      modifier += feature.value; // value is already negative
    }
  });

  return modifier;
};

// ============================================
// SPECIAL FORM BUILDERS
// ============================================

/**
 * Build a Legendary Beast form from a base Tier 1 form
 * @param {Object} baseForm - Base Tier 1 form
 * @returns {Object} Enhanced legendary form
 */
export const buildLegendaryBeast = async (baseForm) => {
  const data = await loadBeastformData();
  const config = data.specialForms?.legendaryBeast;
  if (!config) return null;

  // Enhance trait bonus
  const enhancedTraitBonuses = {};
  Object.entries(baseForm.traitBonus || {}).forEach(([trait, bonus]) => {
    enhancedTraitBonuses[trait] = bonus + config.bonuses.trait;
  });

  // Parse and enhance damage
  const baseDamage = baseForm.attack?.damage || 'd4';
  const enhancedDamage = addDamageBonus(baseDamage, config.bonuses.damage);

  return {
    id: `legendary-${baseForm.id}`,
    name: `Legendary ${baseForm.name}`,
    tier: 3,
    isBuilt: true,
    baseFormId: baseForm.id,
    examples: baseForm.examples?.map(e => `Legendary ${e}`),
    traitBonus: enhancedTraitBonuses,
    evasionBonus: baseForm.evasionBonus + config.bonuses.evasion,
    attack: {
      ...baseForm.attack,
      name: `Legendary ${baseForm.attack?.name || 'Attack'}`,
      damage: enhancedDamage
    },
    advantages: [...(baseForm.advantages || [])],
    features: [...(baseForm.features || [])],
    tags: [...(baseForm.tags || []), 'legendary']
  };
};

/**
 * Build a Mythic Beast form from a base Tier 1-2 form
 * @param {Object} baseForm - Base Tier 1-2 form
 * @returns {Object} Enhanced mythic form
 */
export const buildMythicBeast = async (baseForm) => {
  const data = await loadBeastformData();
  const config = data.specialForms?.mythicBeast;
  if (!config) return null;

  // Enhance trait bonus
  const enhancedTraitBonuses = {};
  Object.entries(baseForm.traitBonus || {}).forEach(([trait, bonus]) => {
    enhancedTraitBonuses[trait] = bonus + config.bonuses.trait;
  });

  // Parse and enhance damage with die upgrade
  const baseDamage = baseForm.attack?.damage || 'd4';
  let enhancedDamage = addDamageBonus(baseDamage, config.bonuses.damage);
  if (config.bonuses.dieUpgrade) {
    enhancedDamage = upgradeDamageDie(enhancedDamage);
  }

  return {
    id: `mythic-${baseForm.id}`,
    name: `Mythic ${baseForm.name}`,
    tier: 4,
    isBuilt: true,
    baseFormId: baseForm.id,
    examples: baseForm.examples?.map(e => `Mythic ${e}`),
    traitBonus: enhancedTraitBonuses,
    evasionBonus: baseForm.evasionBonus + config.bonuses.evasion,
    attack: {
      ...baseForm.attack,
      name: `Mythic ${baseForm.attack?.name || 'Attack'}`,
      damage: enhancedDamage
    },
    advantages: [...(baseForm.advantages || [])],
    features: [...(baseForm.features || [])],
    tags: [...(baseForm.tags || []), 'mythic']
  };
};

/**
 * Build a Hybrid form from multiple base forms
 * @param {Array} baseForms - Array of base forms to combine
 * @param {number} tier - Hybrid tier (3 for Legendary, 4 for Mythic)
 * @param {Array} selectedAdvantages - Player-selected advantages
 * @param {Array} selectedFeatures - Player-selected features
 * @returns {Object} Combined hybrid form
 */
export const buildHybridForm = async (baseForms, tier, selectedAdvantages, selectedFeatures) => {
  const data = await loadBeastformData();
  const configKey = tier === 4 ? 'mythicHybrid' : 'legendaryHybrid';
  const config = data.specialForms?.[configKey];
  if (!config) return null;

  // Combine trait bonuses (take highest for each trait)
  const combinedTraitBonuses = {};
  baseForms.forEach(form => {
    Object.entries(form.traitBonus || {}).forEach(([trait, bonus]) => {
      combinedTraitBonuses[trait] = Math.max(combinedTraitBonuses[trait] || 0, bonus);
    });
  });

  // Take best evasion bonus + tier bonus
  const bestEvasion = Math.max(...baseForms.map(f => f.evasionBonus || 0));
  const tierBonus = tier === 4 ? 2 : 1;

  // Select best attack (highest damage)
  const bestAttack = baseForms.reduce((best, form) => {
    const formDamage = parseDamageValue(form.attack?.damage);
    const bestDamage = parseDamageValue(best?.damage);
    return formDamage > bestDamage ? form.attack : best;
  }, baseForms[0]?.attack);

  const formIds = baseForms.map(f => f.id);
  const formNames = baseForms.map(f => f.name);

  return {
    id: `hybrid-${formIds.join('-')}`,
    name: `${tier === 4 ? 'Mythic' : 'Legendary'} Hybrid`,
    description: `Hybrid of ${formNames.join(' + ')}`,
    tier,
    isBuilt: true,
    isHybrid: true,
    baseFormIds: formIds,
    traitBonus: combinedTraitBonuses,
    evasionBonus: bestEvasion + tierBonus,
    attack: bestAttack ? {
      ...bestAttack,
      name: 'Hybrid Attack'
    } : null,
    advantages: selectedAdvantages,
    features: selectedFeatures,
    stressCost: config.stressCost,
    tags: ['hybrid', tier === 4 ? 'mythic' : 'legendary']
  };
};

/**
 * Get available advantages from combined forms for hybrid building
 * @param {Array} baseForms - Array of base forms
 * @returns {Array} Unique advantages from all forms
 */
export const getHybridAdvantagePool = (baseForms) => {
  const advantages = new Set();
  baseForms.forEach(form => {
    (form.advantages || []).forEach(adv => advantages.add(adv));
  });
  return Array.from(advantages);
};

/**
 * Get available features from combined forms for hybrid building
 * @param {Array} baseForms - Array of base forms
 * @returns {Array} All features from all forms
 */
export const getHybridFeaturePool = (baseForms) => {
  const features = [];
  baseForms.forEach(form => {
    (form.features || []).forEach(feature => {
      // Avoid duplicates by ID
      if (!features.some(f => f.id === feature.id)) {
        features.push({ ...feature, sourceForm: form.name });
      }
    });
  });
  return features;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Add a flat bonus to a damage string
 * @param {string} damage - Damage string like "d8" or "d10+4"
 * @param {number} bonus - Bonus to add
 * @returns {string} Updated damage string
 */
const addDamageBonus = (damage, bonus) => {
  if (!damage) return `d4+${bonus}`;

  // Match patterns like "d8", "d10+4", "2d6+3"
  const match = damage.match(/^(\d*)d(\d+)(?:\+(\d+))?/);
  if (!match) return damage;

  const count = match[1] || '1';
  const die = match[2];
  const existingBonus = parseInt(match[3] || '0', 10);

  const newBonus = existingBonus + bonus;
  return newBonus > 0 ? `${count}d${die}+${newBonus}` : `${count}d${die}`;
};

/**
 * Upgrade a damage die (d4 -> d6 -> d8 -> d10 -> d12)
 * @param {string} damage - Damage string
 * @returns {string} Upgraded damage string
 */
const upgradeDamageDie = (damage) => {
  if (!damage) return damage;

  const dieUpgrades = { '4': '6', '6': '8', '8': '10', '10': '12', '12': '12' };

  return damage.replace(/d(\d+)/, (match, die) => {
    const upgradedDie = dieUpgrades[die] || die;
    return `d${upgradedDie}`;
  });
};

/**
 * Parse damage string to approximate numeric value for comparison
 * @param {string} damage - Damage string
 * @returns {number} Approximate damage value
 */
const parseDamageValue = (damage) => {
  if (!damage) return 0;

  const match = damage.match(/^(\d*)d(\d+)(?:\+(\d+))?/);
  if (!match) return 0;

  const count = parseInt(match[1] || '1', 10);
  const die = parseInt(match[2], 10);
  const bonus = parseInt(match[3] || '0', 10);

  // Average damage = count * (die/2 + 0.5) + bonus
  return count * ((die / 2) + 0.5) + bonus;
};

// ============================================
// EXPORTS
// ============================================

export const beastformService = {
  // Data access
  loadBeastformData,
  getAvailableForms,
  getFormsGroupedByTier,
  getFormById,
  getSpecialFormBuilder,
  getMaxTierForLevel,

  // Transformation
  canTransform,
  transform,
  dropForm,

  // Stats
  getTransformedStats,
  hasBeastformFeature,
  hasBeastformMechanic,
  getBeastformThresholdModifier,

  // Special form builders
  buildLegendaryBeast,
  buildMythicBeast,
  buildHybridForm,
  getHybridAdvantagePool,
  getHybridFeaturePool
};

export default beastformService;
